import { useState } from 'react';
import {
  CasBaseDialog,
  CasDisplayField,
  CasRenderDate,
  CasRenderRelativeDateTime,
  CasStackRow,
  CasCRUDTable,
  CasPanel,
  CasButton,
  CasLoaderWithBackdrop,
} from 'src/compass/shared/components';
import { IGeneralObjectType } from 'src/compass/site/util';
import { DateFormat, formatDate } from 'src/compass/shared/util/casMoment';
import useRetrieve from 'src/compass/shared/hooks/reactQuery/useRetrieve';

import DeadReportsForm from './DeadReportsForm';

interface DeadReportsDialogProps {
  data?: IGeneralObjectType;
  id?: string;
  onClose: VoidFunction;
  onViewClaimDetails?: VoidFunction;
}

export default function DeadReportsDialog({
  id,
  data: dataFromParent,
  onClose,
  onViewClaimDetails,
}: DeadReportsDialogProps) {
  const [reloadTable, setReloadTable] = useState<Date | null>(null);

  if (id && dataFromParent) throw Error("Can't specify both id and data.");
  if (!id && !dataFromParent) throw Error('Either id or data must be specified.');

  const { data: dataFromQuery, isLoading, isError } = useRetrieve({
    url: 'Sce/Claims',
    params: { id },
    enabled: !!id,
  });

  if (isLoading) return <CasLoaderWithBackdrop />;
  if (isError) throw new Error('Error retrieving data from the server.');

  const data = id ? dataFromQuery : dataFromParent;

  return (
    <CasBaseDialog open onClose={onClose} title="Dead Reports" showCloseIcon>
      <CasStackRow>
        <CasDisplayField label="Insured Name" value={data?.insuredName} />
        <CasDisplayField label="Year" value={data?.reinsuranceYear} />
        <CasDisplayField label="Policy" value={data?.policyNumber} />
        <CasDisplayField label="Sce" value={data?.sce} />
        <CasDisplayField label="Status" value={data?.status} />
        <CasDisplayField
          label="Effective"
          value={formatDate(data?.effectiveDate, DateFormat.MDY)}
        />
        <CasDisplayField label="End" value={formatDate(data?.endDate, DateFormat.MDY)} />
      </CasStackRow>

      <CasPanel color='primary'>
        <DeadReportsForm 
          key={reloadTable?.toString()} // Logic for form Clear.
          showAddButton
          onSaved={()=> {setReloadTable(new Date())}}  
          editFormAdditionalData={{ endorsementId: data.id }}
        />
      </CasPanel>

      {id && (
        <CasButton variant="text" onClick={onViewClaimDetails} sx={{ fontWeight: 'bold' }}>
          View Claim Details
        </CasButton>
      )}
      <CasCRUDTable
        entityTitle="Dead Report"
        entity="Sce/Claims/DeadReports"
        defaultOrder='desc'
        reloadTable={reloadTable}
        showAddButton={false}
        columns={[
          { field: 'date', render: CasRenderDate, showEditOnclick: true },
          { field: 'count', render: ({row}) => row?.unspecified ? "Unspecified" : row?.count },
          { field: 'entered', render: CasRenderRelativeDateTime },
          { field: 'by' },
        ]}
        editForm={DeadReportsForm}
        canDelete={false}
        additionalPayload={{ endorsementId:data.id }}
        includeExport={false}
        editFormAdditionalData={{ endorsementId: data.id }}
      />
    </CasBaseDialog>
  );
}
